import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type DraggingPosition,
  StaticTreeDataProvider,
  type TreeItem,
  type TreeItemIndex,
} from "react-complex-tree";

import {
  type IMyDocumentsItem,
  type IMyDocumentsResponse,
} from "./useMyDocuments";

export const TREE_ID = "my-documents-tree";
export const MOVE_TREE_ID = "my-documents-move-tree";
export const ROOT_PARENT = "/";

export interface IMyDocumentsTreeData extends IMyDocumentsItem {
  childCount: number;
  isRoot?: boolean;
}

export type MyDocumentsActionItem = IMyDocumentsItem & {
  isFolder: boolean;
};

interface IUseMyDocumentsTreeParams {
  data?: IMyDocumentsResponse;
  moveTarget: MyDocumentsActionItem | null;
  moveFile: (parent: string, target: string) => Promise<any>;
  moveFolder: (parent: string, target: string) => Promise<any>;
}

interface IPendingMove {
  id: string;
  destinationParentId: string;
  isFolder: boolean;
}

type ITreeViewState = Record<
  string,
  | {
      expandedItems?: TreeItemIndex[];
      selectedItems?: TreeItemIndex[];
      focusedItem?: TreeItemIndex;
    }
  | undefined
>;

type ITreeItems = Record<TreeItemIndex, TreeItem<IMyDocumentsTreeData>>;

export const findRootFolder = (
  documents: IMyDocumentsItem[],
): IMyDocumentsItem | undefined =>
  documents.find(doc => doc.type === 0) ??
  documents.find(doc => doc.type !== 2 && doc.name === ROOT_PARENT);

export const findRootFolderId = (
  documents: IMyDocumentsItem[],
): string | undefined => findRootFolder(documents)?.id;

export const getDocumentLabel = (
  item: Pick<IMyDocumentsTreeData, "id" | "name" | "isRoot">,
  rootId?: string,
) => {
  if (item.isRoot ?? (rootId && item.id === rootId)) return ROOT_PARENT;
  return item.name || "Untitled folder";
};

const createRootTreeData = (
  rootFolder: IMyDocumentsItem,
): IMyDocumentsTreeData => ({
  ...rootFolder,
  name: ROOT_PARENT,
  parent: ROOT_PARENT,
  childCount: 0,
  isRoot: true,
});

const isRootParent = (parent: string | undefined, rootId: string) =>
  !parent || parent === ROOT_PARENT || parent === rootId;

const resolveTreeParentId = (
  parent: string | undefined,
  rootId: string,
  treeItems: Record<TreeItemIndex, TreeItem<IMyDocumentsTreeData>>,
) => {
  if (isRootParent(parent, rootId)) return rootId;
  if (parent && treeItems[parent]) return parent;
  return rootId;
};

export const normalizeDestinationParentId = (
  destination: string,
  rootId?: string,
) => {
  if (!rootId) return destination;
  return destination === ROOT_PARENT || destination === rootId
    ? rootId
    : destination;
};

export const toApiParent = (parentId: string, rootId: string) =>
  parentId === rootId ? ROOT_PARENT : parentId;

const buildTreeItems = (documents: IMyDocumentsItem[]) => {
  const rootFolder = findRootFolder(documents);
  const rootId = rootFolder?.id;

  if (!rootFolder || !rootId) {
    const items: ITreeItems = {};
    return {
      items,
      folderIds: [] as TreeItemIndex[],
      rootId: undefined,
    };
  }

  const sorted = [...documents].sort((left, right) => {
    if (left.type !== right.type) return left.type - right.type;
    const leftName = left.name ?? "";
    const rightName = right.name ?? "";
    return leftName.localeCompare(rightName);
  });

  const treeItems: Record<TreeItemIndex, TreeItem<IMyDocumentsTreeData>> = {
    [rootId]: {
      index: rootId,
      isFolder: true,
      canMove: false,
      canRename: false,
      children: [],
      data: createRootTreeData(rootFolder),
    },
  };

  sorted.forEach(doc => {
    if (doc.id === rootId) return;

    const isFolder = doc.type !== 2;
    treeItems[doc.id] = {
      index: doc.id,
      isFolder,
      canMove: true,
      canRename: false,
      children: [],
      data: {
        ...doc,
        parent: doc.parent ?? ROOT_PARENT,
        childCount: 0,
      },
    };
  });

  sorted.forEach(doc => {
    if (doc.id === rootId) return;

    const item = treeItems[doc.id];
    const parentId = resolveTreeParentId(doc.parent, rootId, treeItems);
    const parentItem = treeItems[parentId];

    parentItem.children = [...(parentItem.children ?? []), doc.id];
    if (parentItem.data) {
      parentItem.data.childCount += 1;
    }

    if (item.data) {
      item.data.childCount = item.children?.length ?? 0;
    }
  });

  const folderIds = Object.values(treeItems)
    .filter(item => item.isFolder)
    .map(item => item.index)
    .filter((id): id is TreeItemIndex => Boolean(id));

  return {
    items: treeItems,
    folderIds,
    rootId,
  };
};

const buildMoveTreeItems = (
  documents: IMyDocumentsItem[],
  moveTarget: MyDocumentsActionItem | null,
) => {
  const rootFolder = findRootFolder(documents);
  const rootId = rootFolder?.id;

  if (!rootFolder || !rootId) {
    const moveItems: ITreeItems = {};
    return {
      moveItems,
      moveExpandedItems: [] as TreeItemIndex[],
      rootId: undefined,
    };
  }

  const excludedIds = new Set<string>();

  if (moveTarget?.isFolder) {
    excludedIds.add(moveTarget.id);
    let expanded = true;

    while (expanded) {
      expanded = false;
      documents.forEach(item => {
        if (
          item.type !== 2 &&
          item.parent &&
          excludedIds.has(item.parent) &&
          !excludedIds.has(item.id)
        ) {
          excludedIds.add(item.id);
          expanded = true;
        }
      });
    }
  }

  const folders = documents.filter(
    item => item.type !== 2 && !excludedIds.has(item.id),
  );

  const moveItems: Record<TreeItemIndex, TreeItem<IMyDocumentsTreeData>> = {
    [rootId]: {
      index: rootId,
      isFolder: true,
      canMove: false,
      canRename: false,
      children: [],
      data: createRootTreeData(rootFolder),
    },
  };

  folders.forEach(folder => {
    if (folder.id === rootId) return;

    moveItems[folder.id] = {
      index: folder.id,
      isFolder: true,
      canMove: true,
      canRename: false,
      children: [],
      data: {
        ...folder,
        parent: folder.parent ?? ROOT_PARENT,
        childCount: 0,
      },
    };
  });

  folders.forEach(folder => {
    if (folder.id === rootId) return;

    const parentId = resolveTreeParentId(folder.parent, rootId, moveItems);
    const parentItem = moveItems[parentId];

    parentItem.children = [...(parentItem.children ?? []), folder.id];
    if (parentItem.data) {
      parentItem.data.childCount += 1;
    }
  });

  const folderIds = Object.values(moveItems)
    .filter(item => item.isFolder)
    .map(item => item.index)
    .filter((id): id is TreeItemIndex => Boolean(id));

  return {
    moveItems,
    moveExpandedItems: folderIds,
    rootId,
  };
};

export const useMyDocumentsTree = ({
  data,
  moveTarget,
  moveFile,
  moveFolder,
}: IUseMyDocumentsTreeParams) => {
  const shiftPressedRef = useRef(false);
  const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);
  const [focusedItem, setFocusedItem] = useState<TreeItemIndex | undefined>();
  const [mainTreeViewState, setMainTreeViewState] = useState<ITreeViewState>(
    {},
  );
  const { items, folderIds, rootId } = useMemo(() => {
    const documents = data?.list ?? [];

    return buildTreeItems(documents);
  }, [data]);
  const { moveItems, moveExpandedItems } = useMemo(() => {
    const documents = data?.list ?? [];

    return buildMoveTreeItems(documents, moveTarget);
  }, [data, moveTarget]);

  const moveFileRef = useRef(moveFile);
  const moveFolderRef = useRef(moveFolder);

  useEffect(() => {
    moveFileRef.current = moveFile;
  }, [moveFile]);

  useEffect(() => {
    moveFolderRef.current = moveFolder;
  }, [moveFolder]);

  const moveDataProvider = useMemo(
    () => new StaticTreeDataProvider(moveItems),
    [moveItems],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        shiftPressedRef.current = true;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        shiftPressedRef.current = false;
      }
    };

    const handleWindowBlur = () => {
      shiftPressedRef.current = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  useEffect(() => {
    if (!rootId) return;

    const validItemIds = new Set(Object.keys(items));

    setSelectedItems(current =>
      current.filter(id => id === rootId || validItemIds.has(String(id))),
    );
  }, [items, rootId]);

  useEffect(() => {
    if (!rootId) return;

    setMainTreeViewState(current => {
      const currentExpanded = current[TREE_ID]?.expandedItems ?? [];
      const currentFolderSet = new Set(folderIds);
      const nextExpanded = currentExpanded.filter(
        id => id === rootId || currentFolderSet.has(id),
      );

      if (!nextExpanded.includes(rootId)) {
        nextExpanded.unshift(rootId);
      }

      return {
        ...current,
        [TREE_ID]: {
          ...current[TREE_ID],
          expandedItems: nextExpanded,
          selectedItems,
          focusedItem,
        },
      };
    });
  }, [folderIds, focusedItem, rootId, selectedItems]);

  const handleDrop = useCallback(
    async (
      droppedItems: Array<TreeItem<IMyDocumentsTreeData>>,
      target: DraggingPosition,
    ) => {
      if (!target || !droppedItems || droppedItems.length === 0 || !rootId) {
        return;
      }

      const destination = (() => {
        if (target.targetType === "root") return ROOT_PARENT;

        if (target.targetType === "item") {
          const targetItem = items[target.targetItem];
          if (targetItem && targetItem.isFolder) {
            return String(target.targetItem);
          }

          return target.parentItem ? String(target.parentItem) : ROOT_PARENT;
        }

        if (target.targetType === "between-items") {
          return target.parentItem ? String(target.parentItem) : ROOT_PARENT;
        }

        return ROOT_PARENT;
      })();
      const destinationParentId = normalizeDestinationParentId(
        destination,
        rootId,
      );

      const moves: IPendingMove[] = [];

      for (const dropped of droppedItems) {
        const id = dropped.data?.id;
        if (!id || id === rootId) continue;

        const currentParent = resolveTreeParentId(
          dropped.data?.parent,
          rootId,
          items,
        );

        if (currentParent === destinationParentId) continue;

        moves.push({
          id,
          destinationParentId,
          isFolder: Boolean(dropped.isFolder),
        });
      }

      if (moves.length === 0) return;

      setPendingMoves(current => [...current, ...moves]);
    },
    [items, rootId],
  );

  const [pendingMoves, setPendingMoves] = useState<IPendingMove[]>([]);

  const executePendingMoves = useCallback(async () => {
    const movesToExecute = [...pendingMoves];
    if (movesToExecute.length === 0 || !rootId) return;

    for (const m of movesToExecute) {
      try {
        const apiParent = toApiParent(m.destinationParentId, rootId);

        if (m.isFolder) {
          await moveFolderRef.current(apiParent, m.id);
        } else {
          await moveFileRef.current(apiParent, m.id);
        }
      } catch {
        // swallow individual errors and continue
      }
    }

    setPendingMoves([]);
  }, [pendingMoves, rootId]);

  const clearPendingMoves = useCallback(() => setPendingMoves([]), []);

  const handleSelectItems = useCallback(
    (nextSelectedItems: TreeItemIndex[]) => {
      setSelectedItems(current => {
        const additiveSelection = shiftPressedRef.current
          ? Array.from(new Set([...current, ...nextSelectedItems]))
          : nextSelectedItems;

        setMainTreeViewState(viewCurrent => ({
          ...viewCurrent,
          [TREE_ID]: {
            ...viewCurrent[TREE_ID],
            selectedItems: additiveSelection,
          },
        }));

        return additiveSelection;
      });
    },
    [],
  );

  const handleFocusItem = useCallback(
    (item: TreeItem<IMyDocumentsTreeData>) => {
      setFocusedItem(item.data.id);
      setMainTreeViewState(current => ({
        ...current,
        [TREE_ID]: {
          ...current[TREE_ID],
          focusedItem: item.data.id,
        },
      }));
    },
    [],
  );

  const handleExpandItem = useCallback(
    (item: TreeItem<IMyDocumentsTreeData>) => {
      setMainTreeViewState(current => {
        const expandedItems = current[TREE_ID]?.expandedItems ?? [];

        if (expandedItems.includes(item.index)) {
          return current;
        }

        return {
          ...current,
          [TREE_ID]: {
            ...current[TREE_ID],
            expandedItems: [...expandedItems, item.index],
          },
        };
      });
    },
    [],
  );

  const handleCollapseItem = useCallback(
    (item: TreeItem<IMyDocumentsTreeData>) => {
      if (item.index === rootId || item.data.isRoot) return;

      setMainTreeViewState(current => {
        const expandedItems = current[TREE_ID]?.expandedItems ?? [];

        return {
          ...current,
          [TREE_ID]: {
            ...current[TREE_ID],
            expandedItems: expandedItems.filter(id => id !== item.index),
          },
        };
      });
    },
    [rootId],
  );

  return {
    items,
    folderIds,
    rootId,
    moveItems,
    moveExpandedItems,
    moveDataProvider,
    selectedItems,
    focusedItem,
    viewState: mainTreeViewState,
    setSelectedItems,
    setFocusedItem,
    handleDrop,
    handleSelectItems,
    handleFocusItem,
    handleExpandItem,
    handleCollapseItem,
    pendingMoves,
    executePendingMoves,
    clearPendingMoves,
  };
};
